import { motion } from "framer-motion";
import { Trophy, BadgeCheck, Globe2, Users, Building2, Wallet } from "lucide-react";
import { IMAGES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: EASE },
};

const REASONS = [
  {
    icon: Trophy,
    title: "Ranked #2 in Delhi",
    desc: "Recognised as one of Delhi's best hotel management colleges, year after year.",
  },
  {
    icon: BadgeCheck,
    title: "100% Placement Record",
    desc: "Every graduating batch placed in leading hotels, resorts and cruise lines.",
  },
  {
    icon: Globe2,
    title: "Global Exposure",
    desc: "International internships and placements across France, Dubai, Mauritius, Maldives and New Zealand.",
  },
  {
    icon: Users,
    title: "Faculty from Industry",
    desc: "Learn from chefs and hoteliers with decades of five-star experience behind them.",
  },
  {
    icon: Wallet,
    title: "Earn While You Learn",
    desc: "Our Earn While You Learn programme lets students earn during their studies — real income before they even graduate.",
  },
  {
    icon: Building2,
    title: "State-of-the-art Infrastructure",
    desc: "Training kitchens, bakery labs and mock front-office setups that mirror real hotels.",
  },
];

function ChapterNumber({ n }) {
  return (
    <span className="font-mono text-sm tracking-[0.3em] text-brand-terra">
      {n} <span aria-hidden="true">——</span>
    </span>
  );
}

export default function Manifesto() {
  return (
    <section id="about" data-testid="about-section" className="mx-auto max-w-7xl px-6 py-28 md:py-36">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
        <motion.div {...reveal} className="lg:col-span-4">
          <ChapterNumber n="01" />
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-brand-terra">
            The Institute
          </p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl">
            A legacy of hospitality, since 2008.
          </h2>
        </motion.div>
        <motion.div {...reveal} className="lg:col-span-8">
          <p className="font-serif text-2xl font-light leading-snug text-brand-ink md:text-3xl">
            Abhi Institute of Hotel Management is one of India's apex educational
            institutions — built on a single belief: that world-class hoteliers are
            made, not born.
          </p>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-brand-muted md:text-lg">
            Founded in 2008 in the heart of Dwarka, New Delhi, ABHI focuses on
            infusing technical and managerial skills into every student — through
            hands-on kitchen training, live front-office simulations, and
            internships with the finest hotels in India and abroad.
          </p>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            <motion.figure
              {...reveal}
              className="overflow-hidden rounded-md"
            >
              <img
                src={IMAGES.kitchen}
                alt="Students training in the ABHI culinary kitchen"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                Training Kitchen · Dwarka Campus
              </figcaption>
            </motion.figure>
            <motion.figure
              {...reveal}
              transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
              className="overflow-hidden rounded-md sm:mt-14"
            >
              <img
                src={IMAGES.lobby}
                alt="ABHI students practising restaurant service in the F&B lab"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                F&B Service Lab · Dwarka Campus
              </figcaption>
            </motion.figure>
          </div>
        </motion.div>
      </div>

      <div className="mt-28 grid gap-12 lg:grid-cols-12 lg:gap-10 md:mt-36">
        <motion.div {...reveal} className="lg:col-span-4">
          <ChapterNumber n="02" />
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-brand-terra">
            Why ABHI
          </p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl">
            Chosen by Delhi's most ambitious hoteliers.
          </h2>
        </motion.div>
        <div className="lg:col-span-8">
          {REASONS.map((reason, i) => (
            <motion.div
              key={reason.title}
              {...reveal}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
              data-testid={`reason-${i}`}
              className="group flex items-start gap-6 border-t border-brand-line py-7 last:border-b"
            >
              <reason.icon className="mt-1 h-6 w-6 shrink-0 text-brand-terra transition-transform duration-300 group-hover:scale-110" />
              <div className="grid gap-1 sm:grid-cols-[220px_1fr] sm:gap-8">
                <h3 className="font-serif text-xl text-brand-ink md:text-2xl">{reason.title}</h3>
                <p className="text-sm leading-relaxed text-brand-muted md:text-base">{reason.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
